import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-4880a963"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "checkout-items" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "checkout-items-title" }, "Your order", -1))
const _hoisted_3 = { class: "checkout-items-line" }
const _hoisted_4 = { class: "checkout-items-line-top" }
const _hoisted_5 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_6 = { key: 1 }
const _hoisted_7 = {
  key: 0,
  class: "checkout-items-line-bottom"
}
const _hoisted_8 = { class: "original-price" }
const _hoisted_9 = {
  key: 1,
  class: "checkout-items-line-bottom"
}
const _hoisted_10 = {
  key: 0,
  class: "checkout-items-line"
}
const _hoisted_11 = { class: "checkout-items-line-top" }
const _hoisted_12 = { class: "checkout-item-with-tooltip" }
const _hoisted_13 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_14 = { key: 1 }
const _hoisted_15 = {
  key: 0,
  class: "checkout-items-line-bottom"
}
const _hoisted_16 = {
  key: 1,
  class: "checkout-items-line"
}
const _hoisted_17 = { class: "checkout-items-line-top" }
const _hoisted_18 = { class: "checkout-item-with-tooltip" }
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Processing Fees", -1))
const _hoisted_20 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_21 = { key: 1 }
const _hoisted_22 = { class: "checkout-items-line" }
const _hoisted_23 = { class: "checkout-items-line-top checkout-items-line-total" }
const _hoisted_24 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, "Total", -1))
const _hoisted_25 = {
  key: 0,
  class: "loading-container"
}
const _hoisted_26 = { key: 1 }
const _hoisted_27 = {
  key: 0,
  class: "checkout-items-line-top checkout-items-line-total"
}
const _hoisted_28 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", null, /*#__PURE__*/_toDisplayString(''), -1))
const _hoisted_29 = {
  key: 1,
  class: "checkout-items-line-bottom"
}

import { formatEther, formatUnits } from '@ethersproject/units';
import { computed } from 'vue';
import { MANIFOLD_FEE_EXPLANATION, PROCESSING_FEES_EXPLANATION } from '@/common/constants';
import Tooltip from '@/components/Tooltip.vue';
import { formatUSDCentsToDollars } from '@/lib/currency';
import { useClaimStore } from '@/store/claimStore';

interface CheckoutItemsProps {
  showInFiat?: boolean;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CheckoutItems',
  props: {
    showInFiat: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;



const store = useClaimStore();

const ercTwenty = computed(() => {
  return store.erc20Address ? store.erc20Symbol : '';
});

const manifoldFeeCurrencyString = computed(() => {
  // Manifold fee is always in networks native currency (ETH, BETH, OETH, etc)
  let currencySymbol;
  if (store.networkId === 10) {
    currencySymbol = 'OETH';
  } else if (store.networkId === 137) {
    currencySymbol = 'MATIC';
  } else {
    currencySymbol = 'ETH';
  }

  return currencySymbol;
});

const currencyString = computed(() => {
  if (ercTwenty.value) {
    return ercTwenty.value;
  }

  return manifoldFeeCurrencyString.value;
});

const priceSubtotalString = computed((): string => {
  if (isNaN(store.tokensToPurchase)) {
    return '';
  }

  if (!props.showInFiat) {
    return `${(+formatUnits(store.priceSubtotal, store.erc20Decimals ?? 18)).toFixed(6)} ${
      currencyString.value
    }`;
  }

  if (hasFakeDiscount.value) {
    return `$${fakeDiscountedPriceString.value}`;
  }

  return `$${formatUSDCentsToDollars(store.priceSubtotalInFiat)}`;
});

const originalPriceString = computed((): string => {
  return `$${formatUSDCentsToDollars(store.priceSubtotalInFiat)}`;
});

const manifoldFeeString = computed((): string => {
  if (isNaN(store.tokensToPurchase)) {
    return '';
  }

  if (props.showInFiat) {
    return `$${formatUSDCentsToDollars(store.manifoldFeeInFiat)}`;
  }

  return `${formatEther(store.manifoldFee)} ${manifoldFeeCurrencyString.value}`;
});

const finalPriceString = computed((): string => {
  if (props.showInFiat) {
    return `$${formatUSDCentsToDollars(store.finalPriceInFiat)}`;
  }

  if (ercTwenty.value) {
    return manifoldFeeString.value;
  }

  return `${(+formatEther(store.finalPrice)).toFixed(6)} ${currencyString.value}`;
});

const processingFeesString = computed(() => {
  return `$${formatUSDCentsToDollars(store.processingFeesInFiat)}`;
});

const priceSubtotalUsdConversionString = computed((): string => {
  return store.priceSubtotalUsdConversion ? `${store.priceSubtotalUsdConversion.toFixed(2)}` : '';
});

const manifoldFeeUsdConversionString = computed((): string => {
  return store.manifoldFeeUsdConversion ? `${store.manifoldFeeUsdConversion.toFixed(2)}` : '';
});

const finalPriceUsdConversionString = computed((): string => {
  return store.finalPriceUsdConversion ? `${store.finalPriceUsdConversion.toFixed(2)}` : '';
});

/**
 * Processing fees can only be negative if we have a fixed USD price on the item and the value of ETH goes down making
 * the cost + gas fees cheaper than the actual fixed USD price. In this case, we will show the user that they are
 * getting a discount on the item.
 */
const hasFakeDiscount = computed(() => {
  return store.processingFeesInFiat.isNegative();
});

const fakeDiscountedPriceString = computed(() => {
  return formatUSDCentsToDollars(store.priceSubtotalInFiat.add(store.processingFeesInFiat));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("span", null, "Item x " + _toDisplayString(_unref(store).tokensToPurchase), 1),
        (_unref(store).loadingFiatPrices && props.showInFiat)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5))
          : (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(priceSubtotalString.value), 1))
      ]),
      (hasFakeDiscount.value && props.showInFiat && !_unref(store).loadingFiatPrices)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("span", _hoisted_8, _toDisplayString(originalPriceString.value), 1)
          ]))
        : (!!priceSubtotalUsdConversionString.value && !props.showInFiat)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createElementVNode("span", null, "~$" + _toDisplayString(priceSubtotalUsdConversionString.value), 1)
            ]))
          : _createCommentVNode("", true)
    ]),
    (_unref(store).hasManifoldFees)
      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("span", null, "Manifold Fee x " + _toDisplayString(_unref(store).tokensToPurchase), 1),
              _createVNode(Tooltip, {
                header: "Manifold Fee",
                content: _unref(MANIFOLD_FEE_EXPLANATION)
              }, null, 8, ["content"])
            ]),
            (_unref(store).loadingFiatPrices && props.showInFiat)
              ? (_openBlock(), _createElementBlock("div", _hoisted_13))
              : (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(manifoldFeeString.value), 1))
          ]),
          (!!manifoldFeeUsdConversionString.value && !props.showInFiat)
            ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
                _createElementVNode("span", null, "~$" + _toDisplayString(manifoldFeeUsdConversionString.value), 1)
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (_unref(store).hasProcessingFees && props.showInFiat)
      ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("div", _hoisted_18, [
              _hoisted_19,
              _createVNode(Tooltip, {
                header: "Processing Fees",
                content: _unref(PROCESSING_FEES_EXPLANATION)
              }, null, 8, ["content"])
            ]),
            (_unref(store).loadingFiatPrices)
              ? (_openBlock(), _createElementBlock("div", _hoisted_20))
              : (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(processingFeesString.value), 1))
          ])
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_22, [
      _createElementVNode("div", _hoisted_23, [
        _hoisted_24,
        (_unref(store).loadingFiatPrices && props.showInFiat)
          ? (_openBlock(), _createElementBlock("div", _hoisted_25))
          : (_openBlock(), _createElementBlock("span", _hoisted_26, _toDisplayString(finalPriceString.value), 1))
      ]),
      (ercTwenty.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
            _hoisted_28,
            _createElementVNode("span", null, _toDisplayString(priceSubtotalString.value), 1)
          ]))
        : _createCommentVNode("", true),
      (!!finalPriceUsdConversionString.value && !props.showInFiat)
        ? (_openBlock(), _createElementBlock("div", _hoisted_29, [
            _createElementVNode("span", null, "~$" + _toDisplayString(finalPriceUsdConversionString.value), 1)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})